import { useEffect, useState } from 'react'
import { haptic } from '../lib/telegram'
import { inviteLinks, shareInvite } from '../lib/referral'
import { api } from '../lib/api'
import { useSirr } from '../store'

interface RefInfo {
  code: string
  invited: number
  /** Сколько звёзд уже принесли приглашения. Считает сервер. */
  earned: number
  /** Сколько друзей вознаграждается; дальше приглашать можно, но без награды. */
  cap: number
  /** Звёзд за одного друга. */
  perFriend: number
  welcomeStars: number
}

/** Отдельный экран приглашений: прогресс к максимуму и кнопка «поделиться». */
export function Invites({ onLogin }: { onLogin: () => void }) {
  const { t, anon } = useSirr()
  const [info, setInfo] = useState<RefInfo | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (anon) return // кода у анонима нет — сервер его и не выдаёт
    api.referral().then(setInfo).catch(() => {})
  }, [anon])

  /*
    Анониму — объяснение и вход, а не код. Награда за друга живёт только на
    настоящем аккаунте: анонимный заводится бесплатно и без счёта, и платить на
    него значило бы завести кран звёзд. Раз платить не будем, то и код выдавать
    нельзя — это обещание, которого мы не выполним.
  */
  if (anon) {
    return (
      <div className="screen">
        <div style={{ border: '1px solid var(--line)', borderRadius: 14, padding: '16px 18px' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{t('anonToInvite')}</div>
          <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginTop: 6, lineHeight: 1.45 }}>
            {t('anonToInviteWhy')}
          </div>
          <button
            type="button"
            onClick={onLogin}
            style={{
              marginTop: 14,
              width: '100%',
              background: 'transparent',
              color: 'var(--ink)',
              border: '1px solid var(--line-strong)',
              borderRadius: 12,
              padding: '11px 20px',
              fontFamily: 'var(--sans)',
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            {t('enter')}
          </button>
        </div>
      </div>
    )
  }

  if (!info) return <div className="screen" />

  const toMax = Math.max(0, info.cap - info.invited)
  // Прогресс считаем по друзьям, а не по звёздам: потолок задан числом друзей.
  const pct = Math.min(100, Math.round((info.invited / info.cap) * 100))

  const share = async () => {
    haptic.select()
    const how = await shareInvite(info.code, t('inviteMsg'))
    if (how === 'copied') flashCopied()
  }
  const copy = async () => {
    haptic.select()
    try {
      await navigator.clipboard.writeText(inviteLinks(info.code).pwa)
      flashCopied()
    } catch {
      /* нет буфера */
    }
  }
  const flashCopied = () => {
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="screen" style={{ textAlign: 'center' }}>
      {/* Эмблема */}
      <div
        style={{
          width: 72,
          height: 72,
          margin: '8px auto 14px',
          borderRadius: '50%',
          border: '1px solid rgba(200,162,78,.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 30,
          color: 'var(--gold)',
          boxShadow: 'var(--gold-glow)',
          background: 'radial-gradient(circle, rgba(58,30,92,.7), rgba(18,10,31,.9))',
        }}
      >
        ✦
      </div>

      <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, margin: 0, whiteSpace: 'pre-line', lineHeight: 1.3 }}>
        {t('inviteHeadline')}
      </h2>

      {/* Прогресс к максимуму */}
      <div style={{ margin: '22px 4px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--ink-soft)' }}>
          <span>{t('inviteProgress', { n: info.invited, cap: info.cap })}</span>
          <span style={{ color: 'var(--gold)' }}>{t('inviteEarned', { b: info.earned })}</span>
        </div>
        <div style={{ height: 10, borderRadius: 6, background: 'rgba(255,255,255,.1)', marginTop: 8, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--gold-gradient)', transition: 'width .4s' }} />
        </div>
        <div style={{ fontSize: 13, color: 'var(--gold)', marginTop: 10 }}>
          {toMax > 0
            ? t('inviteToMax', { n: toMax, max: info.cap * info.perFriend })
            : t('inviteMaxed', { max: info.cap * info.perFriend })}
        </div>
      </div>

      <p className="prose" style={{ fontSize: 13, color: 'var(--ink-muted)', lineHeight: 1.5, margin: '22px 6px' }}>
        {t('inviteHow', { bonus: info.perFriend, stars: info.welcomeStars })}
      </p>

      <button
        onClick={() => void share()}
        style={{
          width: '100%',
          background: 'var(--gold-gradient)',
          color: '#1a1020',
          border: 'none',
          borderRadius: 14,
          padding: '14px',
          fontFamily: 'var(--sans)',
          fontSize: 16,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: 'var(--gold-glow)',
        }}
      >
        {copied ? t('inviteCopied') : t('inviteShare')}
      </button>

      <button
        onClick={() => void copy()}
        style={{
          width: '100%',
          marginTop: 10,
          background: 'transparent',
          color: 'var(--ink-muted)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '12px',
          fontFamily: 'var(--sans)',
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        {t('inviteCopyLink')}
      </button>
    </div>
  )
}
